import React from 'react';
import { Controller } from 'react-hook-form';
import { Button, Form, Header, Icon, Label, List, Message, Segment } from 'semantic-ui-react';
import { CONTACT_METHOD_LABELS, GOAL_DETAILS_PLACEHOLDERS, OTHER_GOAL, PRIMARY_GOAL_OPTIONS } from '../constants';

const FIELD_LABELS = {
    business_name: 'Business name',
    industry_id: 'Industry',
    description: 'How your business operates'
};

const getSuggestionText = ({ value, display }) => display || value;

const WebsiteFindings = ({ findings, onApply, onDismiss }) => {
    const { insights, suggestions } = findings;
    if (!insights) {
        return (
            <Message info onDismiss={onDismiss}>
                We couldn't read <b>{findings.website}</b>. You can fill in the details below yourself.
            </Message>
        );
    }
    return (
        <Message info className='Onboarding__website-findings'>
            <Message.Header>Here's what we learned from {findings.website}</Message.Header>
            <List>
                <List.Item>
                    <List.Icon name='briefcase' />
                    <List.Content><b>Services:</b> {insights.services.join(', ')}</List.Content>
                </List.Item>
                <List.Item>
                    <List.Icon name='comments outline' />
                    <List.Content>
                        <b>How customers reach you:</b>{' '}
                        {insights.contact_methods.map(method => <Label key={method} size='tiny' basic>{CONTACT_METHOD_LABELS[method]}</Label>)}
                    </List.Content>
                </List.Item>
                {insights.listed_hours && (
                    <List.Item>
                        <List.Icon name='clock outline' />
                        <List.Content><b>Hours on your site:</b> {insights.listed_hours}</List.Content>
                    </List.Item>
                )}
            </List>
            {suggestions.length > 0 && (
                <>
                    <p><b>We'll also fill in:</b></p>
                    <List bulleted>
                        {suggestions.map(suggestion => (
                            <List.Item key={suggestion.field}>
                                <b>{FIELD_LABELS[suggestion.field]}:</b> {getSuggestionText(suggestion)}
                            </List.Item>
                        ))}
                    </List>
                </>
            )}
            <p>We'll use this to tailor your recommendations. Fields you've already filled in won't change.</p>
            <Button size='tiny' primary onClick={onApply}>Use This</Button>
            <Button size='tiny' basic onClick={onDismiss}>Dismiss</Button>
        </Message>
    );
}

const PREFILL_LABELS = { business_name: 'business name', website: 'website', industry_id: 'industry' };

const describeFields = (fields) => fields.map(field => PREFILL_LABELS[field]).join(' and ');

const BusinessProfileForm = ({
    control,
    industryOptions,
    primaryGoal,
    prefilledFields = [],
    lockedFields = [],
    websiteInsights,
    onRemoveInsights,
    isReadingCompany,
    isValid,
    onDiscoverWebsite,
    isDiscoveringWebsite,
    websiteFindings,
    onApplyFindings,
    onDismissFindings,
    onGenerate,
    isGeneratingRecommendations
}) => (
    <Segment>
        <Header as='h3'>
            Tell Us About Your Business
            <Header.Subheader>We'll use this to recommend a setup for your account. Nothing is applied until you approve it.</Header.Subheader>
        </Header>
        {(lockedFields.length > 0 || prefilledFields.length > 0) && (
            <Message info size='small'>
                <Icon name='building outline' />
                {lockedFields.length > 0 && <>Your {describeFields(lockedFields)} come{lockedFields.length === 1 ? 's' : ''} from your company profile. To change {lockedFields.length === 1 ? 'it' : 'them'}, go to Settings → Account Management → Company Profile. </>}
                {prefilledFields.length > 0 && <>We filled in your {describeFields(prefilledFields)} from your company profile; you can edit it here.</>}
            </Message>
        )}
        <Form noValidate loading={isGeneratingRecommendations}>
            <Controller
                name='business_name'
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input
                        label='Business Name'
                        name={name}
                        value={value}
                        onChange={onChange}
                        readOnly={isReadingCompany || lockedFields.includes('business_name')}
                        loading={isReadingCompany}
                        icon={lockedFields.includes('business_name') ? 'lock' : null}
                        className={lockedFields.includes('business_name') ? 'Onboarding__locked-field' : ''}
                        placeholder='Enter your business name'
                        required
                        error={error?.message}
                    />
                )}
            />
            <Controller
                name='website'
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input
                        label='Website (optional)'
                        name={name}
                        value={value}
                        onChange={onChange}
                        placeholder='www.yourbusiness.com'
                        error={error?.message}
                        action={{
                            content: websiteInsights ? 'Analyze Again' : 'Analyze Website',
                            icon: 'magic',
                            type: 'button',
                            loading: isDiscoveringWebsite,
                            disabled: !value || !!error || isDiscoveringWebsite,
                            onClick: onDiscoverWebsite
                        }}
                    />
                )}
            />
            {websiteFindings && (
                <WebsiteFindings findings={websiteFindings} onApply={onApplyFindings} onDismiss={onDismissFindings} />
            )}
            {!websiteFindings && websiteInsights && (
                <p className='Onboarding__hint Onboarding__website-used'>
                    <Icon name='check circle' color='green' />
                    Using what we learned from {websiteInsights.website} for your recommendations.{' '}
                    <button type='button' className='Onboarding__link-button' onClick={onRemoveInsights}>Don't use it</button>
                </p>
            )}
            <Form.Group>
                <Controller
                    name='industry_id'
                    control={control}
                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                        <Form.Select
                            width={8}
                            label='Industry'
                            name={name}
                            value={value}
                            options={industryOptions}
                            disabled={lockedFields.includes('industry_id')}
                            search
                            loading={isReadingCompany}
                            onChange={(_, { value }) => onChange({ target: { name, value } })}
                            placeholder='Select industry'
                            required
                            error={error?.message}
                        />
                    )}
                />
            </Form.Group>
            <Controller
                name='primary_goal'
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Field required error={!!error}>
                        <label>Primary Goal</label>
                        {PRIMARY_GOAL_OPTIONS.map(option => (
                            <Form.Radio
                                key={option.key}
                                label={option.text}
                                name={name}
                                value={option.value}
                                checked={value === option.value}
                                onChange={(_, { value }) => onChange({ target: { name, value } })}
                            />
                        ))}
                    </Form.Field>
                )}
            />
            <Controller
                name='goal_details'
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.TextArea
                        label={primaryGoal === OTHER_GOAL ? 'Describe your goal' : 'Tell us more about this goal (optional)'}
                        required={primaryGoal === OTHER_GOAL}
                        name={name}
                        value={value}
                        rows={2}
                        onChange={onChange}
                        placeholder={GOAL_DETAILS_PLACEHOLDERS[primaryGoal] || 'What would make DYL a success for you?'}
                        error={error?.message}
                    />
                )}
            />
            <Controller
                name='description'
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.TextArea
                        label='How does your business operate? (optional)'
                        name={name}
                        value={value}
                        onChange={onChange}
                        placeholder='e.g. We get most of our leads from our website and split them between 10 agents.'
                        error={error?.message}
                    />
                )}
            />
            <Button primary floated='right' type='button' disabled={!isValid || isGeneratingRecommendations} onClick={onGenerate}>
                <Icon name='magic' /> Generate Setup Recommendations
            </Button>
            <div style={{ clear: 'both' }} />
        </Form>
    </Segment>
);

export default BusinessProfileForm;
